import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SvgTrash from 'components/svg/SvgTrash';
import { useToast } from 'context/ToastContext';
import { useNavigate } from 'react-router-dom';
import SvgList from 'components/svg/SvgList';
import SvgCheckSquare from 'components/svg/SvgCheckSquare';
import useDeleteLead from '../hooks/hookDeleteLead';
import * as apiLead from 'api/apiLeads';
import { useLead } from '../context/LeadProvider';
import SvgUser from 'components/svg/SvgUser';
import type { LeadType } from '@econtractor/types';
import useSettingsParams from 'hooks/useSettingsParams';
import SolarIcon from 'components/icons/SolarIcon';
import vconst from 'utils/helpers/structs/vconst';
import { canViewSales } from 'utils/helpers/access/hasUserAccess';
import { useAuth } from 'context/AuthContext';
import { getObjectCardPath } from 'utils/helpers/objects/objectCardPath';
import { buildNewSaleFromLead } from '../../LeadCard/helpers/proposalAmountToObjectPrice';
import type { NewSaleType } from 'modules/objects';

interface LeadActionProps {
    lead: LeadType;
    fetchLeads: (force?: boolean) => Promise<void | null>;
    setClientInfoId: (value: number | null) => void;
    setTasksLeadId: (value: number | null) => void;
    setNewSale?: (sale: NewSaleType) => void;
}

const LeadAction = ({ lead, fetchLeads, setClientInfoId, setTasksLeadId, setNewSale }: LeadActionProps) => {
    const { showToast } = useToast();
    const { t } = useTranslation(['Leads', 'Clients', 'Objects', 'common']);
    const { setShowSpinner } = useLead();
    const navigate = useNavigate();
    const { deleteLead } = useDeleteLead();
    const params = useSettingsParams();
    const { auth } = useAuth();
    const canShowTasks = params.tasksModule && params.tasksForLeads;
    const canCreateSale = canViewSales(auth?.user);
    const hasObject = Number(lead.objectId || 0) > 0;
    const isLinkedSale = Number(lead.onlySale) === 1;

    const cbDeleteLead = async () => {
        setShowSpinner(true);
        const resDelete = await apiLead.deleteLead(lead.ID);
        await fetchLeads(true);
        if (resDelete) showToast(t('messLeadDeleted'), 'success');
        else showToast(t('common:messError'), 'danger');
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${options?.danger ? ' text-danger' : ''}`}>
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgList />,
                t('leadPopup.events'),
                t('leadPopup.eventsHint'),
                () => navigate(`/leads/${lead.ID}`),
            )}

            {canShowTasks &&
                renderMenuItem(
                    <SvgCheckSquare width={16} height={16} />,
                    t('leadPopup.task'),
                    t('leadPopup.taskHint'),
                    () => setTasksLeadId(lead.ID),
                )}

            {renderMenuItem(
                <SvgUser />,
                t('leadPopup.client'),
                t('Clients:clientPopup.viewHint'),
                () => setClientInfoId(lead.clientId),
            )}

            {hasObject && lead.leadStatus === vconst.leadAccept
                ? renderMenuItem(
                    <SolarIcon name={isLinkedSale ? 'wallet2' : 'brightness-high'} />,
                    isLinkedSale ? t('toolGoToSale') : t('toolGoToObject'),
                    isLinkedSale ? t('toolGoToSaleHint') : t('toolGoToObjectHint'),
                    () => navigate(getObjectCardPath(lead.objectId || 0, isLinkedSale)),
                )
                : null}

            {!hasObject && canCreateSale && setNewSale
                ? renderMenuItem(
                    <SolarIcon name="wallet2" />,
                    t('toolCreateSale'),
                    t('toolCreateSaleHint'),
                    () => setNewSale(buildNewSaleFromLead(lead)),
                )
                : null}

            {lead.objectId == 0 && (
                <>
                    <Dropdown.Divider className="my-2" />

                    {renderMenuItem(
                        <SvgTrash />,
                        t('leadPopup.delete'),
                        t('leadPopup.deleteHint'),
                        () => deleteLead(lead, cbDeleteLead),
                        { danger: true },
                    )}
                </>
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle className="p-1 py-0" size="sm" variant="light-secondary" id="dropdown-basic">
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default LeadAction;
